import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, easeOut, AnimatePresence } from "framer-motion";

import { Typography, Button } from '@mui/material';
import sessionstorage from 'sessionstorage';

import Classes from "./SectionA.module.css";
import '../Home.css'

import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import Navbar from './Navbar/Navbar';
import { lightFromCorner } from '../../../../utils/motionVariants';
import backend from '../../../../api';
import StoreSelectionPopup from '../../../GlobalComp/Footer/StoreSelectionPopup';

const IOS_APP_LINK =
  'https://apps.apple.com/us/app/fuelclubs/id6753362541';
const ANDROID_APP_LINK =
  'https://play.google.com/store/apps/details?id=com.devzona.fuelclubs';
const DEFAULT_BOOKING_PAUSE_LABEL = 'Service Update';
const DEFAULT_BOOKING_PAUSE_MESSAGE =
  "Dear customers, due to military obligations of the owner, we are pausing services until summer of 2027.";

function SectionA() {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  const [showStorePopup, setShowStorePopup] = useState(false);

  const { user } = useSelector((state) => state.auth);
  const onboarding = useSelector((state) => state.companyInfo.info);
  const companyId = onboarding?._id;

  const navigate = useNavigate();

  const navigateToSignup = () => {
    navigate('/signup');
  };

  const updateDimensions = () => {
    if (typeof window !== 'undefined') setWidth(window.innerWidth);
  };

  // ----- Fetch service (same approach as Services.jsx) -----
  const [service, setService] = useState(null);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  const [pauseLabel, setPauseLabel] = useState(DEFAULT_BOOKING_PAUSE_LABEL);
  const [pauseMessage, setPauseMessage] = useState(DEFAULT_BOOKING_PAUSE_MESSAGE);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!companyId) return;
      try {
        setLoadingPrices(true);
        const { data } = await backend.get(`/api/services/by-company/${companyId}`);
        if (!cancelled && data?.ok) setService(data.data || null);
      } catch (e) {
        if (!cancelled) {
          console.error('SectionA: failed to fetch service prices', e?.message || e);
          setService(null);
        }
      } finally {
        !cancelled && setLoadingPrices(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await backend.get('/api/services/pause-status');
        if (!cancelled && data?.ok) {
          setShowNotice(!!(data?.servicePause?.showNotice ?? data?.servicePause?.active));
          setPauseLabel(data?.servicePause?.label || DEFAULT_BOOKING_PAUSE_LABEL);
          setPauseMessage(data?.servicePause?.message || DEFAULT_BOOKING_PAUSE_MESSAGE);
        }
      } catch {
        // keep defaults if status endpoint is unavailable
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build octane list from service.prices with sensible fallbacks
  const OCTANE_PRICES = useMemo(() => {
    const reg = Number(service?.prices?.regular ?? 4.5);
    const mid = Number(service?.prices?.mid ?? 5.0);
    const prem = Number(service?.prices?.premium ?? 5.5);

    // ✅ Diesel should NOT show "89"
    return [
      { id: 'regular', label: 'Regular', octane: '87', price: reg },
      { id: 'mid', label: 'Diesel', octane: null, price: mid }, // <-- removed 89
      { id: 'premium', label: 'Premium', octane: '91–93', price: prem },
    ];
  }, [service]);

  const [idx, setIdx] = useState(0);

  // rotate every 3s
  useEffect(() => {
    if (!OCTANE_PRICES?.length) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % OCTANE_PRICES.length);
    }, 3000);
    return () => clearInterval(t);
  }, [OCTANE_PRICES]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateDimensions);
      return () => window.removeEventListener('resize', updateDimensions);
    }
  }, []);

  const compactVariants = {
    initial: { y: "100%", opacity: 0, scale: 0.98 },
    animate: { y: "0%", opacity: 1, scale: 1, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } },
    exit: { y: "-100%", opacity: 0, scale: 0.98, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } },
  };

  const active =
    OCTANE_PRICES[Math.min(idx, Math.max(0, OCTANE_PRICES.length - 1))] ||
    { price: 0, label: 'Regular', octane: '87', id: 'regular' };

  const isMobile = width < 768;

  const handleStoreSelect = (platform) => {
    const url = platform === 'ios' ? IOS_APP_LINK : ANDROID_APP_LINK;
    window.open(url, '_blank');
  };

  const isDiesel = (active?.label || '').toLowerCase() === 'diesel';

  return (
    <>
      <section id='home' className={Classes.home} >
        <div className={Classes.customBox}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: .3, ease: easeOut, delay: 0.4 }}
          >
            <Navbar />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: .35, ease: easeOut, delay: 0.45 }}
          >
            <h1 className={Classes.Txt1} >
              <span>Gas</span> Delivery <br /> Service
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: .4, ease: easeOut, delay: .5 }}
            whileTap={{ scale: 0.90 }}
            className={Classes.home_btns_con}
          >
            <div className={Classes.home_btns}>
              {!isMobile && (
                <motion.div whileTap={{ scale: 0.97 }} whileHover={{ scale: 1.03 }}>
                  <button
                    className={`${Classes.whiteBtn} ${Classes.coloredBtn}`}
                    onClick={navigateToSignup}
                  >
                    <h5>Get started on web</h5>
                  </button>
                </motion.div>
              )}

              <motion.div whileTap={{ scale: 0.97 }} whileHover={{ scale: 1.03 }}>
                <button
                  className={Classes.whiteBtn}
                  onClick={() => setShowStorePopup(true)}
                >
                  <h5>Get started on mobile</h5>
                </button>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            whileTap={{ scale: 0.90 }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: .3, ease: easeOut, delay: .55 }}
          >
            {showNotice ? (
              <div className={Classes.pauseNoticeWrap}>
                <div className={Classes.pauseNotice}>
                  <span className={Classes.pauseNoticeBadge}>{pauseLabel}</span>
                  <h5 className={Classes.pauseNoticeText}>{pauseMessage}</h5>
                </div>
              </div>
            ) : null}
            <motion.div whileTap={{ scale: 0.95 }} className={Classes.miniBanner}>
              {/* Price chip — only the number animates */}
              <button
                type="button"
                title={
                  loadingPrices
                    ? 'Loading latest price…'
                    : isDiesel
                      ? `${active.label}`
                      : `${active.label} ${active.octane} Octane`
                }
              >
                <h5 style={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                  <span>$</span>
                  <span
                    style={{
                      display: "inline-block",
                      position: "relative",
                      overflow: "hidden",
                      height: "1em",
                      lineHeight: 1,
                      minWidth: 20,
                      textAlign: 'left',
                    }}
                  >
                    <AnimatePresence mode="popLayout">
                      <motion.span
                        key={`${active.id}-${active.price}`}
                        variants={compactVariants}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        style={{ display: "inline-block" }}
                      >
                        {Number(active.price || 0).toFixed(2)}
                      </motion.span>
                    </AnimatePresence>
                  </span>
                  <span>/Gallon</span>
                </h5>
              </button>

              {/* Label line:
                  - Regular/Premium: "Label - Octane Octane"
                  - Diesel: "Diesel" only (no 89, no Octane)
               */}
              <h5 style={{ display: "flex", gap: 1, alignItems: "baseline" }} className={Classes.SpanText} >
                <span
                  style={{
                    display: "inline-block",
                    position: "relative",
                    height: "1em",
                    lineHeight: 1,
                  }}
                >
                  <AnimatePresence mode="popLayout">
                    <motion.span
                      key={`label-${active.id}`}
                      variants={compactVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      style={{ display: "inline-block", fontWeight: 700 }}
                    >
                      {active.label}
                    </motion.span>
                  </AnimatePresence>
                </span>

                {!isDiesel && (
                  <>
                    <span>&nbsp;-&nbsp;</span>

                    <span
                      style={{
                        display: "inline-block",
                        position: "relative",
                        overflow: "hidden",
                        height: "1em",
                        lineHeight: 1,
                      }}
                    >
                      <AnimatePresence mode="popLayout">
                        <motion.span
                          key={`octane-${active.octane}`}
                          variants={compactVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                          style={{ display: "inline-block", fontWeight: 700 }}
                        >
                          {active.octane}
                        </motion.span>
                      </AnimatePresence>
                    </span>

                    <span>&nbsp;Octane</span>
                  </>
                )}
              </h5>
            </motion.div>
          </motion.div>

        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: .4, ease: easeOut, delay: 0.3 }}
          className={Classes.gradientBackground}
        >
          <motion.img
            src='/assets/images/ellipse-left.webp'
            draggable={false}
            loading='lazy'
            decoding="async"
            className={Classes.lightLeft}
            {...lightFromCorner('left', 0.1)}
          />

          <motion.img
            src='/assets/images/ellipse-right.webp'
            draggable={false}
            loading='lazy'
            decoding="async"
            className={Classes.lightRight}
            {...lightFromCorner('right', 0.1)}
          />
        </motion.div>
      </section>

      {/* Reusable Store Selection Popup */}
      <StoreSelectionPopup
        open={showStorePopup}
        onClose={() => setShowStorePopup(false)}
        onSelect={handleStoreSelect}
      />
    </>
  );
}

export default SectionA;
